import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
  onSnapshot,
  serverTimestamp,
  query,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  db,
  auth,
  storage,
  OperationType,
  handleFirestoreError,
  checkCurrentUserAdminStatus,
} from './firebase';
import { apiFetch } from './apiConfig';
import {
  SupportedDocumentType,
  CompanionDocumentRecord,
  CompanionVerificationProfile,
  DOCUMENT_TYPE_METADATA,
  verificationRequirements,
  VerificationAuditEvent,
  DocumentVerificationStatus,
  VerificationOverallStatus,
} from '../types/verification.types';
import {
  mapFirestoreStateToLifecycleStatus,
  syncCompanionLifecycleInFirestore,
  CompanionProfileLifecycleStatus,
} from '../utils/companionStatusManager';

function isBase64DataUrl(url?: string | null): boolean {
  return typeof url === 'string' && url.trim().toLowerCase().startsWith('data:');
}

export function sanitizeDocumentRecordForFirestore(
  record: CompanionDocumentRecord
): CompanionDocumentRecord {
  const sanitizedFront = { ...record.front };
  if (isBase64DataUrl(sanitizedFront.downloadUrl)) {
    delete sanitizedFront.downloadUrl;
  }
  const sanitizedBack = { ...record.back };
  if (isBase64DataUrl(sanitizedBack.downloadUrl)) {
    delete sanitizedBack.downloadUrl;
  }
  return {
    ...record,
    front: sanitizedFront,
    back: sanitizedBack,
  };
}

async function resolveSideStorageUrl(side: {
  uploaded: boolean;
  storagePath?: string;
  downloadUrl?: string;
}): Promise<string | undefined> {
  if (side.downloadUrl && !isBase64DataUrl(side.downloadUrl)) {
    return side.downloadUrl;
  }
  if (side.storagePath && auth.currentUser) {
    try {
      return await getDownloadURL(ref(storage, side.storagePath));
    } catch {
      return undefined;
    }
  }
  return undefined;
}

async function hydrateDocumentStorageUrls(
  record: CompanionDocumentRecord
): Promise<CompanionDocumentRecord> {
  const sanitized = sanitizeDocumentRecordForFirestore(record);
  const [frontUrl, backUrl] = await Promise.all([
    resolveSideStorageUrl(sanitized.front),
    resolveSideStorageUrl(sanitized.back),
  ]);
  return {
    ...sanitized,
    front: {
      ...sanitized.front,
      ...(frontUrl ? { downloadUrl: frontUrl } : {}),
    },
    back: {
      ...sanitized.back,
      ...(backUrl ? { downloadUrl: backUrl } : {}),
    },
  };
}

// Default document skeleton
export function createDefaultDocumentRecord(
  docType: SupportedDocumentType
): CompanionDocumentRecord {
  const meta = DOCUMENT_TYPE_METADATA[docType];
  return {
    id: docType,
    documentType: docType,
    title: meta.title,
    description: meta.helpText,
    badge: meta.badge,
    idFormatHint: meta.idPlaceholder,
    front: {
      uploaded: false,
    },
    back: {
      required: meta.backRequired,
      uploaded: false,
      notApplicable: !meta.backRequired,
    },
    status: 'not_uploaded',
    rejectionReason: null,
    adminComment: null,
    idNumber: '',
    legalName: '',
  };
}

// Default verification state for a companion
export function createDefaultVerificationProfile(
  companionId: string,
  extra?: Partial<CompanionVerificationProfile>
): CompanionVerificationProfile {
  return {
    companionId,
    name: extra?.name || 'Companion Member',
    email: extra?.email || '',
    phone: extra?.phone || '',
    city: extra?.city || 'Kolkata, India',
    avatarUrl: extra?.avatarUrl || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=400&q=80',
    verificationStatus: 'not_started',
    identityStatus: 'not_uploaded',
    selfieStatus: 'not_started',
    faceStatus: 'not_started',
    bankStatus: 'not_started',
    kycStatus: 'not_started',
    verificationProgress: 0,
    isVerified: false,
    rejectionReason: null,
    ...extra,
  };
}

// Local cache key helper
function getLocalKey(companionId: string) {
  return `pairup_verification_${companionId}`;
}

function getLocalDocsKey(companionId: string) {
  return `pairup_verification_docs_${companionId}`;
}

// Calculate progress percentage according to current verificationRequirements
export function calculateVerificationProgress(
  docs: Record<SupportedDocumentType, CompanionDocumentRecord>,
  verification: CompanionVerificationProfile
): { progress: number; identityStatus: DocumentVerificationStatus; overallStatus: VerificationOverallStatus } {
  const allDocs = Object.values(docs || {});
  const uploadedDocs = allDocs.filter((d) => d.front?.uploaded || d.status !== 'not_uploaded');
  const anyUploaded = uploadedDocs.length > 0;
  const anyPending = uploadedDocs.some((d) => d.status === 'pending');
  const anyRejected = uploadedDocs.some((d) => d.status === 'rejected' || d.status === 'reupload_required');

  // A primary document is completely verified if its status is 'verified',
  // front is uploaded, and back is uploaded (if required).
  const verifiedPrimaryDocs = allDocs.filter((d) => {
    if (d.status !== 'verified') return false;
    if (!d.front?.uploaded) return false;
    const isBackRequired = d.back?.required && !d.back?.notApplicable;
    if (isBackRequired && !d.back?.uploaded) return false;
    return true;
  });

  let identityStatus: DocumentVerificationStatus = 'not_uploaded';

  // Strict invariant: overall identity can ONLY be 'verified' if:
  // 1. At least one complete primary government ID document is verified (front + back if required).
  // 2. Zero submitted/uploaded documents are in 'rejected' or 'reupload_required' status.
  // 3. Zero submitted/uploaded documents are in 'pending' status.
  if (verifiedPrimaryDocs.length > 0 && !anyRejected && !anyPending) {
    identityStatus = 'verified';
  } else if (anyRejected) {
    identityStatus = 'reupload_required';
  } else if (anyPending) {
    identityStatus = 'pending';
  } else if (anyUploaded) {
    identityStatus = 'uploaded';
  }

  // Weight for Phase 1: Identity is the primary required module (100% of required scope)
  let progress = 0;
  if (identityStatus === 'verified') {
    progress = 100;
  } else if (identityStatus === 'pending') {
    progress = 75;
  } else if (identityStatus === 'uploaded') {
    progress = 50;
  } else if (anyUploaded) {
    progress = 25;
  }

  let overallStatus: VerificationOverallStatus = 'not_started';
  if (identityStatus === 'verified') {
    overallStatus = 'verified';
  } else if (identityStatus === 'pending') {
    overallStatus = 'under_review';
  } else if (identityStatus === 'uploaded') {
    overallStatus = 'submitted';
  } else if (identityStatus === 'reupload_required') {
    overallStatus = 'rejected';
  } else if (anyUploaded) {
    overallStatus = 'in_progress';
  }

  return { progress, identityStatus, overallStatus };
}

// ================= REAL-TIME FIRESTORE LISTENER =================
export function subscribeToCompanionVerification(
  companionId: string,
  onUpdate: (profile: CompanionVerificationProfile) => void
): () => void {
  if (!companionId) return () => {};

  const companionRef = doc(db, 'companions', companionId);
  try {
    return onSnapshot(
      companionRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          onUpdate({
            ...createDefaultVerificationProfile(companionId),
            ...data,
            companionId,
          } as CompanionVerificationProfile);
        } else {
          onUpdate(createDefaultVerificationProfile(companionId));
        }
      },
      (err) => {
        console.warn('subscribeToCompanionVerification Firestore error:', err);
        onUpdate(createDefaultVerificationProfile(companionId));
      }
    );
  } catch (error) {
    console.warn('Snapshot listener could not attach:', error);
    return () => {};
  }
}

export function subscribeToCompanionDocuments(
  companionId: string,
  onUpdate: (docs: Record<SupportedDocumentType, CompanionDocumentRecord>) => void
): () => void {
  if (!companionId) return () => {};

  const docsColRef = collection(db, 'companions', companionId, 'documents');
  try {
    return onSnapshot(
      docsColRef,
      async (snapshot) => {
        const loaded: Partial<Record<SupportedDocumentType, CompanionDocumentRecord>> = {};
        for (const snapDoc of snapshot.docs) {
          const data = snapDoc.data() as CompanionDocumentRecord;
          if (data.documentType && DOCUMENT_TYPE_METADATA[data.documentType]) {
            loaded[data.documentType] = await hydrateDocumentStorageUrls(data);
          }
        }

        // Ensure all supported documents exist in the map
        const completeDocs: Record<SupportedDocumentType, CompanionDocumentRecord> = {
          aadhaar: loaded.aadhaar || createDefaultDocumentRecord('aadhaar'),
          pan: loaded.pan || createDefaultDocumentRecord('pan'),
          voter_id: loaded.voter_id || createDefaultDocumentRecord('voter_id'),
          driving_license: loaded.driving_license || createDefaultDocumentRecord('driving_license'),
          passport: loaded.passport || createDefaultDocumentRecord('passport'),
        };

        onUpdate(completeDocs);
      },
      (err) => {
        console.warn('subscribeToCompanionDocuments Firestore error:', err);
      }
    );
  } catch (error) {
    console.warn('Documents listener could not attach:', error);
    return () => {};
  }
}

// Fetch documents once
export async function getCompanionDocuments(
  companionId: string
): Promise<Record<SupportedDocumentType, CompanionDocumentRecord>> {
  const completeDocs: Record<SupportedDocumentType, CompanionDocumentRecord> = {
    aadhaar: createDefaultDocumentRecord('aadhaar'),
    pan: createDefaultDocumentRecord('pan'),
    voter_id: createDefaultDocumentRecord('voter_id'),
    driving_license: createDefaultDocumentRecord('driving_license'),
    passport: createDefaultDocumentRecord('passport'),
  };

  if (!companionId) return completeDocs;

  try {
    const docsColRef = collection(db, 'companions', companionId, 'documents');
    const snapshot = await getDocs(docsColRef);
    for (const snapDoc of snapshot.docs) {
      const data = snapDoc.data() as CompanionDocumentRecord;
      if (data.documentType && completeDocs[data.documentType]) {
        completeDocs[data.documentType] = await hydrateDocumentStorageUrls(data);
      }
    }
  } catch (err) {
    console.warn('Could not read documents from Firestore:', err);
  }

  return completeDocs;
}

// ================= UPLOAD DOCUMENT SIDE =================
export async function uploadDocumentSide(
  companionId: string,
  documentType: SupportedDocumentType,
  side: 'front' | 'back',
  file: File,
  extraMetadata?: { idNumber?: string; legalName?: string }
): Promise<CompanionDocumentRecord> {
  if (!companionId || !auth.currentUser || auth.currentUser.uid !== companionId) {
    throw new Error('Unauthorized: upload KYC documents only for your own account.');
  }

  if (!DOCUMENT_TYPE_METADATA[documentType]) {
    throw new Error(`Unsupported document type: ${String(documentType)}`);
  }
  if (side !== 'front' && side !== 'back') {
    throw new Error('Invalid document side.');
  }
  if (!file || file.size <= 0 || file.size > 10 * 1024 * 1024) {
    throw new Error('Choose a non-empty document file up to 10 MB.');
  }

  const validTypes = [
    'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf',
  ];
  if (!validTypes.includes(file.type)) {
    throw new Error('Invalid file format. Use JPG, PNG, WEBP, or PDF.');
  }

  const apiDocumentTypes: Record<SupportedDocumentType, string> = {
    aadhaar: 'AADHAAR',
    pan: 'PAN',
    voter_id: 'VOTER_ID',
    driving_license: 'DRIVING_LICENSE',
    passport: 'PASSPORT',
  };

  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
  const firebaseStoragePath = `verification/${companionId}/documents/${documentType}/${side}.${ext}`;
  const storageRef = ref(storage, firebaseStoragePath);

  const token = await auth.currentUser.getIdToken();
  const form = new FormData();
  form.append('documentType', apiDocumentTypes[documentType]);
  form.append(side, file, file.name);
  if (extraMetadata?.idNumber) form.append('idNumber', extraMetadata.idNumber);
  if (extraMetadata?.legalName) form.append('legalName', extraMetadata.legalName);

  const response = await apiFetch('/api/kyc/documents/upload', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });

  const result = await response.json().catch(() => ({}));
  let resolvedStoragePath = '';
  let documentStatus = 'uploaded';

  if (response.ok && result.success === true && result.document?.id) {
    resolvedStoragePath = `/api/kyc/documents/${encodeURIComponent(result.document.id)}/file/${side}`;
    documentStatus = result.document.status === 'UNDER_REVIEW' ? 'pending' : 'uploaded';
  } else if (response.status === 404 || response.status === 503) {
    await uploadBytes(storageRef, file, { contentType: file.type });
    resolvedStoragePath = firebaseStoragePath;
  } else {
    throw new Error(result.message || result.error?.message || 'KYC upload failed.');
  }

  // Return UI-compatible state only. The backend/Neon remains authoritative.
  const currentDocs = await getCompanionDocuments(companionId);
  const existing = currentDocs[documentType] || createDefaultDocumentRecord(documentType);
  const now = new Date().toISOString();

  const updated: CompanionDocumentRecord = {
    ...existing,
    updatedAt: now,
    idNumber: extraMetadata?.idNumber || existing.idNumber || '',
    legalName: extraMetadata?.legalName || existing.legalName || '',
    rejectionReason: null,
    adminComment: null,
    status: documentStatus as DocumentVerificationStatus,
  };

  const fileMetadata = {
    uploaded: true,
    storagePath: resolvedStoragePath,
    downloadUrl: '',
    fileName: file.name,
    fileSize: file.size,
    mimeType: file.type,
    uploadedAt: now,
  };

  if (side === 'front') {
    updated.front = { ...updated.front, ...fileMetadata };
  } else {
    updated.back = { ...updated.back, ...fileMetadata };
  }

  // Do not write KYC document metadata or approval status to Firestore.
  return updated;
}
// Toggle back side not applicable (e.g. for single-sided IDs)
export async function toggleBackNotApplicable(
  companionId: string,
  documentType: SupportedDocumentType,
  notApplicable: boolean
): Promise<CompanionDocumentRecord> {
  if (!auth.currentUser || auth.currentUser.uid !== companionId) {
    throw new Error('Unauthorized: You can only modify your own KYC document settings.');
  }
  const currentDocs = await getCompanionDocuments(companionId);
  const docRecord = sanitizeDocumentRecordForFirestore(
    currentDocs[documentType] || createDefaultDocumentRecord(documentType)
  );

  docRecord.back.notApplicable = notApplicable;
  if (docRecord.front.uploaded && (!docRecord.back.required || notApplicable || docRecord.back.uploaded)) {
    docRecord.status = 'uploaded';
  }

  currentDocs[documentType] = docRecord;

  const docRef = doc(db, 'companions', companionId, 'documents', documentType);
  await setDoc(docRef, docRecord, { merge: true });

  await syncCompanionLifecycleInFirestore(companionId, {
    documents: currentDocs,
    userId: companionId,
  });

  return docRecord;
}

// ================= SUBMIT ALL DOCUMENTS FOR REVIEW =================
export async function submitDocumentsForReview(
  companionId: string,
  companionProfile?: Partial<CompanionVerificationProfile>
): Promise<void> {
  if (!companionId || !auth.currentUser || auth.currentUser.uid !== companionId) {
    throw new Error('Unauthorized: you can only submit your own KYC documents.');
  }

  // Neon-backed API is authoritative. Do not update Firestore KYC status here.
  const token = await auth.currentUser.getIdToken();
  const response = await apiFetch('/api/kyc/verification/submit', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name: companionProfile?.name,
      email: companionProfile?.email,
      phone: companionProfile?.phone,
    }),
  });

  const result = await response.json().catch(() => ({}));
  if (!response.ok || result.success !== true || result.status !== 'UNDER_REVIEW') {
    throw new Error(result.message || result.code || 'KYC submission failed.');
  }
}
async function assertKycAdminPrivileges(fallbackEmail: string): Promise<string> {
  const adminStatus = await checkCurrentUserAdminStatus();
  const normalizedRole = String(adminStatus.role || '').toUpperCase();
  if (!adminStatus.isAdmin || !['SUPER_ADMIN', 'ADMIN', 'KYC_ADMIN'].includes(normalizedRole)) {
    throw new Error('Forbidden: KYC administrator privileges are required to review identity documents.');
  }
  return adminStatus.email || fallbackEmail;
}

// ================= ADMIN APPROVAL =================
export async function adminApproveDocument(
  companionId: string,
  documentType: SupportedDocumentType,
  adminEmail: string = 'admin@pairup.today'
): Promise<void> {
  const verifiedAdminEmail = await assertKycAdminPrivileges(adminEmail);
  const now = new Date().toISOString();
  const currentDocs = await getCompanionDocuments(companionId);
  const docRecord = sanitizeDocumentRecordForFirestore(
    currentDocs[documentType] || createDefaultDocumentRecord(documentType)
  );

  const isBackSatisfied = !docRecord.back.required || docRecord.back.notApplicable || docRecord.back.uploaded;
  if (!docRecord.front.uploaded || !isBackSatisfied) {
    throw new Error('Cannot approve an incomplete or unuploaded identity document.');
  }

  docRecord.status = 'verified';
  docRecord.verifiedAt = now;
  docRecord.verifiedBy = verifiedAdminEmail;
  docRecord.rejectionReason = null;
  docRecord.adminComment = null;
  docRecord.updatedAt = now;

  currentDocs[documentType] = docRecord;

  const docRef = doc(db, 'companions', companionId, 'documents', documentType);
  await setDoc(docRef, docRecord, { merge: true });

  // Check if identity requirements are fully verified
  const { progress, identityStatus, overallStatus } = calculateVerificationProgress(
    currentDocs,
    createDefaultVerificationProfile(companionId)
  );

  const lifecycleStatus: CompanionProfileLifecycleStatus =
    overallStatus === 'verified'
      ? 'verified'
      : mapFirestoreStateToLifecycleStatus({
          documents: currentDocs,
          verificationStatus: overallStatus,
          identityStatus,
          isVerified: false,
        });

  const companionRef = doc(db, 'companions', companionId);
  await setDoc(
    companionRef,
    {
      identityStatus,
      verificationStatus: overallStatus,
      profileStatus: lifecycleStatus,
      lifecycleStatus,
      verificationProgress: progress,
      isVerified: overallStatus === 'verified',
      verifiedAt: overallStatus === 'verified' ? now : null,
      verifiedBy: overallStatus === 'verified' ? verifiedAdminEmail : null,
      verification: {
        overallStatus,
        identityStatus,
        selfieStatus: 'not_started',
        faceMatchStatus: 'not_started',
        bankStatus: 'not_started',
        kycStatus: 'not_started',
        finalStatus: overallStatus === 'verified' ? 'approved' : 'pending',
        verificationProgress: progress,
        isVerified: overallStatus === 'verified',
      },
      schemaVersion: 1,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  // Record audit event in subcollection
  const eventId = `evt_${Date.now()}`;
  const eventRef = doc(db, 'companions', companionId, 'verificationEvents', eventId);
  await setDoc(eventRef, {
    eventId,
    companionId,
    documentType,
    eventType: 'APPROVED',
    adminEmail: verifiedAdminEmail,
    notes: `Document ${DOCUMENT_TYPE_METADATA[documentType].title} verified and approved.`,
    timestamp: now,
  });

  // Record formal review ticket in root verificationReviews
  const reviewId = `rev_${Date.now()}`;
  const reviewRef = doc(db, 'verificationReviews', reviewId);
  await setDoc(reviewRef, {
    reviewId,
    companionId,
    reviewerEmail: verifiedAdminEmail,
    reviewType: 'IDENTITY',
    decision: 'APPROVED',
    reasons: [],
    adminNotes: `Document ${documentType} approved`,
    reviewedAt: now,
  });

  // Record immutable admin action
  const actionId = `act_${Date.now()}`;
  const actionRef = doc(db, 'adminActions', actionId);
  await setDoc(actionRef, {
    actionId,
    adminEmail: verifiedAdminEmail,
    actionType: 'VERIFY_DOCUMENT',
    targetUid: companionId,
    payload: { documentType, decision: 'APPROVED' },
    executedAt: now,
  });

  // Sync authoritative lifecycle status in Firestore via centralized status manager
  await syncCompanionLifecycleInFirestore(companionId, {
    documents: currentDocs,
    userId: companionId,
    context: {
      adminVerdict: overallStatus === 'verified' ? 'verified' : null,
      verificationStatus: overallStatus,
      identityStatus,
    },
  });
}

// ================= ADMIN REJECTION =================
export async function adminRejectDocument(
  companionId: string,
  documentType: SupportedDocumentType,
  reason: string,
  adminComment: string = '',
  adminEmail: string = 'admin@pairup.today'
): Promise<void> {
  const verifiedAdminEmail = await assertKycAdminPrivileges(adminEmail);
  const cleanReason = String(reason || '').trim();
  if (cleanReason.length < 3) {
    throw new Error('A specific rejection reason (at least 3 characters) is required.');
  }

  const now = new Date().toISOString();
  const currentDocs = await getCompanionDocuments(companionId);
  const docRecord = sanitizeDocumentRecordForFirestore(
    currentDocs[documentType] || createDefaultDocumentRecord(documentType)
  );

  docRecord.status = 'reupload_required';
  docRecord.rejectionReason = cleanReason;
  docRecord.adminComment = adminComment;
  docRecord.updatedAt = now;
  docRecord.verifiedAt = null;
  docRecord.verifiedBy = verifiedAdminEmail;

  currentDocs[documentType] = docRecord;

  const docRef = doc(db, 'companions', companionId, 'documents', documentType);
  await setDoc(docRef, docRecord, { merge: true });

  const { progress, identityStatus, overallStatus } = calculateVerificationProgress(
    currentDocs,
    createDefaultVerificationProfile(companionId)
  );

  const companionRef = doc(db, 'companions', companionId);
  await setDoc(
    companionRef,
    {
      identityStatus,
      verificationStatus: overallStatus,
      profileStatus: 'rejected',
      lifecycleStatus: 'rejected',
      verificationProgress: progress,
      rejectionReason: cleanReason,
      verification: {
        overallStatus,
        identityStatus,
        selfieStatus: 'not_started',
        faceMatchStatus: 'not_started',
        bankStatus: 'not_started',
        kycStatus: 'not_started',
        finalStatus: 'rejected',
        verificationProgress: progress,
        isVerified: false,
        rejectionReason: cleanReason,
      },
      schemaVersion: 1,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  // Record audit event in subcollection
  const eventId = `evt_${Date.now()}`;
  const eventRef = doc(db, 'companions', companionId, 'verificationEvents', eventId);
  await setDoc(eventRef, {
    eventId,
    companionId,
    documentType,
    eventType: 'REJECTED',
    adminEmail: verifiedAdminEmail,
    notes: `Reason: ${cleanReason}. Comment: ${adminComment}`,
    timestamp: now,
  });

  // Record formal review ticket in root verificationReviews
  const reviewId = `rev_${Date.now()}`;
  const reviewRef = doc(db, 'verificationReviews', reviewId);
  await setDoc(reviewRef, {
    reviewId,
    companionId,
    reviewerEmail: verifiedAdminEmail,
    reviewType: 'IDENTITY',
    decision: 'REJECTED',
    reasons: [cleanReason],
    adminNotes: adminComment,
    reviewedAt: now,
  });

  // Record immutable admin action
  const actionId = `act_${Date.now()}`;
  const actionRef = doc(db, 'adminActions', actionId);
  await setDoc(actionRef, {
    actionId,
    adminEmail: verifiedAdminEmail,
    actionType: 'REJECT_DOCUMENT',
    targetUid: companionId,
    payload: { documentType, decision: 'REJECTED', reason: cleanReason, adminComment },
    executedAt: now,
  });

  // Sync authoritative lifecycle status in Firestore via centralized status manager
  await syncCompanionLifecycleInFirestore(companionId, {
    documents: currentDocs,
    userId: companionId,
    context: {
      adminVerdict: 'rejected',
      rejectionReason: cleanReason,
      verificationStatus: overallStatus,
      identityStatus,
    },
  });
}

// ================= FETCH ALL FOR ADMIN VERIFICATION CENTER =================
export async function fetchAdminVerificationList(): Promise<CompanionVerificationProfile[]> {
  const adminStatus = await checkCurrentUserAdminStatus();
  if (!adminStatus.isAdmin) {
    throw new Error('Forbidden: Admin authentication is required to view KYC verification records.');
  }

  const list: CompanionVerificationProfile[] = [];
  const q = query(collection(db, 'companions'));
  const snap = await getDocs(q);
  for (const d of snap.docs) {
    const data = d.data() as CompanionVerificationProfile;
    const [docs, bankSnapshot] = await Promise.all([
      getCompanionDocuments(d.id),
      getDoc(doc(db, 'companions', d.id, 'bankVerification', 'status')),
    ]);
    const bankData = bankSnapshot.exists() ? bankSnapshot.data() : null;
    const bankStatus = bankData?.status;
    list.push({
      ...createDefaultVerificationProfile(d.id),
      ...data,
      companionId: d.id,
      documents: docs,
      ...(bankData ? {
        bankStatus: ['verified', 'pending', 'review_required', 'failed', 'unavailable'].includes(bankStatus) ? bankStatus : 'not_started',
        bankVerification: {
          status: ['verified', 'pending', 'review_required', 'failed', 'unavailable'].includes(bankStatus) ? bankStatus : 'unavailable',
          maskedAccount: typeof bankData.maskedAccount === 'string' ? bankData.maskedAccount : '',
          ifsc: typeof bankData.ifsc === 'string' ? bankData.ifsc : '',
          providerRequestId: typeof bankData.providerRequestId === 'string' ? bankData.providerRequestId : null,
          method: typeof bankData.method === 'string' ? bankData.method : '',
          checkedAt: typeof bankData.checkedAt === 'string' ? bankData.checkedAt : '',
          verifiedAt: typeof bankData.verifiedAt === 'string' ? bankData.verifiedAt : null,
        },
      } : {}),
    });
  }

  return list;
}

// ================= FUTURE-READY SERVICE BOUNDARIES =================
/**
 * verifyIdentity()
 * Phase 1 Implementation: Submits identity documents for review.
 */
export async function verifyIdentity(
  companionId: string,
  documents: CompanionDocumentRecord[]
): Promise<{ success: boolean; status: DocumentVerificationStatus }> {
  await submitDocumentsForReview(companionId);
  return { success: true, status: 'pending' };
}

/**
 * verifySelfie()
 * Future Module 2 placeholder â€” ready for seamless integration without database restructuring.
 */
export async function verifySelfie(
  companionId: string,
  _selfieData: { base64: string; livenessScore?: number }
): Promise<{ success: boolean; status: 'pending' | 'verified' | 'rejected'; error?: string }> {
  console.warn(`[VerificationService] verifySelfie provider is not configured for ${companionId}; manual Admin KYC review is required.`);
  return {
    success: false,
    status: 'pending',
    error: 'Automated biometric selfie verification is not configured. Selfie requires manual Admin KYC review.',
  };
}

/**
 * verifyFace()
 * Future Module 3 placeholder â€” server-side biometric face comparison via approved KYC provider.
 */
export async function verifyFace(
  companionId: string,
  _payload: { documentPhotoId: string; selfiePhotoId: string }
): Promise<{ success: boolean; matchResult: 'MATCHED' | 'NOT_MATCHED' | 'REVIEW_REQUIRED'; error?: string }> {
  console.warn(`[VerificationService] verifyFace provider is not configured for ${companionId}; manual Admin KYC review is required.`);
  return {
    success: false,
    matchResult: 'REVIEW_REQUIRED',
    error: 'Automated facial biometric matching is not configured. Manual Admin KYC review is required.',
  };
}

/**
 * verifyBank()
 * Module 4 â€” secure account holder name & pennyless bank verification via backend.
 */
export async function verifyBank(
  companionId: string,
  bankDetails: {
    accountHolderName: string;
    accountNumber: string;
    confirmAccountNumber?: string;
    ifsc: string;
    registeredPhone?: string;
    bankName?: string;
  }
): Promise<{ success: boolean; maskedAccount: string; status: 'verified' | 'pending' | 'review_required' | 'unavailable' | 'failed' }> {
  if (!auth.currentUser || auth.currentUser.uid !== companionId) throw new Error('Please sign in to verify your own account.');
  if (
    bankDetails.confirmAccountNumber !== undefined &&
    bankDetails.confirmAccountNumber !== bankDetails.accountNumber
  ) {
    throw new Error('Account number and Retype account number do not match.');
  }
  const token = await auth.currentUser.getIdToken();
  const response = await apiFetch('/api/companion/bank-verification', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      accountHolderName: bankDetails.accountHolderName,
      accountNumber: bankDetails.accountNumber,
      confirmAccountNumber: bankDetails.confirmAccountNumber ?? bankDetails.accountNumber,
      ifsc: bankDetails.ifsc,
      registeredPhone: bankDetails.registeredPhone,
    }),
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok && response.status !== 503) {
    const message = response.status === 401
      ? 'Your session expired. Please sign in again.'
      : result.code === 'RATE_LIMITED' || result.code === 'DUPLICATE_REQUEST'
      ? 'Please wait before trying again.'
      : 'Bank verification could not be completed.';
    throw new Error(message);
  }
  return {
    success: Boolean(result.success),
    maskedAccount: typeof result.maskedAccount === 'string' ? result.maskedAccount : `â€¢â€¢â€¢â€¢${bankDetails.accountNumber.slice(-4)}`,
    status: ['verified', 'pending', 'review_required', 'unavailable', 'failed'].includes(result.status) ? result.status : 'unavailable',
  };
}

export async function getBankVerificationStatus(companionId: string) {
  if (!auth.currentUser || auth.currentUser.uid !== companionId) throw new Error('Please sign in to view your bank verification status.');
  const token = await auth.currentUser.getIdToken();
  const response = await apiFetch('/api/companion/bank-verification', {
    headers: { Authorization: `Bearer ${token}` },
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || !result.success) {
    throw new Error(response.status === 401
      ? 'Your session expired. Please sign in again.'
      : 'Bank verification status is temporarily unavailable.');
  }
  return result.verification || null;
}

/**
 * runKYC()
 * Master orchestrator for future third-party automated KYC pipeline.
 */
export async function runKYC(
  companionId: string
): Promise<{ overallVerified: boolean; requirements: typeof verificationRequirements }> {
  console.log(`[VerificationService] Future runKYC called for ${companionId}`);
  return { overallVerified: false, requirements: verificationRequirements };
}



